import { twMerge } from 'tailwind-merge';
import {
  getFullTextIUCNStatus,
  getThreatToHumans,
  handleDepth,
  SUB_LINK_IDS,
} from '../../helpers/speciesHelpers';
import { Heading } from '../../../../components/Heading/Heading';
import { Text } from '../../../../components/Text/Text';
import { ExternalLink } from '../../../../components/Clickables/ExternalLink/ExternalLink';
import { GetSingleSpecies_entry } from '../../../../interfaces/graphql';
import { InfoCard } from '../../../../components/Cards/InfoCard/InfoCard';
import { capitalize } from '../../../../helpers/string';
import { useUserInfo } from '../../../../helpers/hooks/useUserInfo';
import { IUCNStatusIcon } from '../../../../components/Icons/IUCNStatusIcon';

interface IProps {
  className?: string;
  species: GetSingleSpecies_entry;
}

export const GeneralInfoSection = ({ className, species }: IProps) => {
  const { user } = useUserInfo();

  const {
    description,
    waterTypes,
    minCommonDepth,
    maxCommonDepth,
    iucnStatus,
    threatToHumans,
    displayName,
    latinName,
  } = species;

  return (
    <div
      id={SUB_LINK_IDS.GENERAL_INFO}
      className={twMerge(
        'w-full scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset)',
        className,
      )}
    >
      <div className="flex w-full flex-col gap-6 px-6 sm:px-0">
        <div className="flex flex-col gap-2">
          <Heading as="h1" variant="2xl" className="m-0">
            <Heading as="span" variant="xs" className="text-midnight mb-0 block">
              Fishing for{' '}
            </Heading>
            {displayName}
          </Heading>
          {latinName && (
            <Heading variant="2xs" as="div" className="text-clownfish-shade">
              {latinName}
            </Heading>
          )}
        </div>
        <Text variant="lg" className={`whitespace-pre-wrap [grid-area:description]`}>
          {description}
        </Text>
        <div data-testid="DetailedInformationSection">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4 md:grid-cols-3">
            {waterTypes.length > 0 && (
              <InfoCard title="Water type" iconText={waterTypes.map(capitalize).join(' / ')} />
            )}
            {maxCommonDepth !== null && minCommonDepth !== null ? (
              <InfoCard
                title="Common depth"
                iconText={handleDepth(minCommonDepth, maxCommonDepth, user)}
              />
            ) : (
              <></>
            )}
            {iucnStatus && (
              <InfoCard
                title="IUCN Status"
                iconText={getFullTextIUCNStatus(iucnStatus)}
                iconComponent={<IUCNStatusIcon status={iucnStatus} />}
                placeIconAboveIconText
              />
            )}
            {threatToHumans && threatToHumans.length > 0 ? (
              <InfoCard
                title="Threat to humans"
                iconText={threatToHumans.map(threat => getThreatToHumans(threat)).join(' / ')}
              />
            ) : (
              <></>
            )}
          </div>
          <Text className="mt-3" variant="xs">
            Source:{' '}
            <ExternalLink
              href={`https://www.fishbase.se/Summary/SpeciesSummary.php?ID=${species.legacyId}`}
            >
              Fishbase
            </ExternalLink>
          </Text>
        </div>
      </div>
    </div>
  );
};
